import * as React from "react";
import { createPortal } from "react-dom";
import { Button, cn } from "@finster/ui";
import { ArrowLeft, Loader2, ScrollText, X } from "lucide-react";
import { getSkillOrigin } from "../catalog";
import { useModalFocus } from "../hooks/useModalFocus";
import type { Skill, SkillViewStatus } from "../types";

export interface SkillDetailViewProps {
  skill: Skill | null;
  status: Exclude<SkillViewStatus, "idle">;
  selected: boolean;
  onBack: () => void;
  onTryInChat: (skill: Skill) => void;
  onClose: () => void;
  open?: boolean;
  tryInChatDisabled?: boolean;
  tryInChatDisabledReason?: string;
  errorMessage?: string;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  className?: string;
}

/**
 * The source filter only splits personal from everything else, so the label
 * follows the same two buckets. Organization and Finster Skills stay distinct
 * origins because that ordering still drives slug dedupe in the catalog.
 */
function getSourceLabel(skill: Skill): string {
  return getSkillOrigin(skill) === "personal" ? "My Skill" : "System Skill";
}

function formatUpdatedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Updated date unavailable";
  return `Updated on ${new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
  }).format(date)}`;
}

export const SkillDetailView: React.FC<SkillDetailViewProps> = ({
  skill,
  status,
  selected,
  onBack,
  onTryInChat,
  onClose,
  open = true,
  tryInChatDisabled = false,
  tryInChatDisabledReason,
  errorMessage = "Unable to load this Skill.",
  returnFocusRef,
  className,
}) => {
  const dialogRef = React.useRef<HTMLElement>(null);
  const titleId = `skill-detail-title-${React.useId().replace(/:/g, "")}`;

  useModalFocus({
    open,
    dialogRef,
    onDismiss: onClose,
    returnFocusRef,
  });

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[2147483646]">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close Skill details"
        onClick={onClose}
        className="absolute inset-0 cursor-default border-0 bg-foreground/60 p-0"
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "absolute inset-0 flex min-h-0 flex-col overflow-hidden bg-background text-foreground outline-none",
          className
        )}
      >
        <header className="flex h-13 min-h-13 items-center justify-between border-b border-border px-4 py-2">
          <Button
            data-autofocus
            type="button"
            variant="outline"
            size="icon"
            onClick={onBack}
            aria-label="Back to Skills"
            className="size-8 cursor-pointer rounded-lg"
          >
            <ArrowLeft className="size-4" aria-hidden />
          </Button>
          <h2 id={titleId} className="sr-only">
            {skill?.name ?? "Skill details"}
          </h2>
          <ScrollText className="size-5 text-muted-foreground" aria-hidden />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close Skill details"
            className="size-8 cursor-pointer"
          >
            <X className="size-5" aria-hidden />
          </Button>
        </header>

        {status === "loading" ? (
          <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading Skill…
          </div>
        ) : status === "error" ? (
          <div
            role="alert"
            className="flex min-h-0 flex-1 items-center justify-center px-4 text-center text-xs text-destructive"
          >
            {errorMessage}
          </div>
        ) : !skill ? (
          <div
            role="alert"
            className="flex min-h-0 flex-1 items-center justify-center px-4 text-center text-xs text-muted-foreground"
          >
            Skill details are unavailable.
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center gap-2 border-b border-border bg-background-secondary p-4 text-center">
              <h3 className="m-0 text-sm font-medium">{skill.name}</h3>
              <p className="m-0 text-xs leading-5 text-muted-foreground">{skill.description}</p>
              <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
                <span>{getSourceLabel(skill)}</span>
                <span className="h-3 w-px bg-border" aria-hidden />
                <span>{formatUpdatedAt(skill.updatedAt)}</span>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={selected || tryInChatDisabled || !skill.enabled}
                title={
                  tryInChatDisabled
                    ? tryInChatDisabledReason
                    : !skill.enabled
                      ? "This Skill is unavailable."
                      : undefined
                }
                onClick={() => onTryInChat(skill)}
                className="h-9 cursor-pointer px-4 text-xs"
              >
                {selected ? "Added to chat" : "Try in chat"}
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto bg-background-secondary p-4">
              <h4 className="m-0 text-xs font-medium">Objective</h4>
              <p className="mt-2 whitespace-pre-wrap text-xs leading-5 text-muted-foreground">
                {skill.instructions || "No instructions provided."}
              </p>
            </div>
          </>
        )}
      </section>
    </div>,
    document.body
  );
};
